import { useCallback, useEffect, useState } from "react";

/**
 * The author's tables.
 *
 * Ordered by `updated_at`, which tables have and blocks never did — so the sort
 * says **Updated** and means it, rather than approximating from a document's
 * date (SPEC.md §4a).
 */

export interface TableSummary {
  id: string;
  caption: string;
  label: string;
  content: unknown;
  createdAt: string;
  updatedAt: string;
  /** How many papers reference it. Zero is a legitimate state, not an error. */
  documentCount: number;
  isOwner: boolean;
  /** Set when this table was made by taking a copy of another. */
  copiedFrom?: string | null;
}

/**
 * The filters the listing offers, counted (Olivia, tables-modern 1c): All · In a
 * paper · Not in a paper · Has problems.
 *
 * "In a paper" is new with the gate gone: it is the list most authors want
 * once any table can go into any paper.
 */
export type TableFilter = "all" | "some" | "none" | "problems";

export function matchesFilter(
  t: TableSummary,
  f: TableFilter,
  /** Supplied by the caller, which knows how to inspect a table's content. */
  hasProblems?: (t: TableSummary) => boolean,
): boolean {
  switch (f) {
    case "some": return t.documentCount > 0;
    case "none": return t.documentCount === 0;
    case "problems": return hasProblems?.(t) ?? false;
    default: return true;
  }
}

/** How the listing is ordered. Recently edited is the default, and the API's order. */
export type TableSort = "edited" | "name" | "papers";

export const SORT_LABELS: Record<TableSort, string> = {
  edited: "Recently edited",
  name: "Name",
  papers: "Most used",
};

export function sortTables(tables: TableSummary[], sort: TableSort): TableSummary[] {
  const out = [...tables];
  const time = (t: TableSummary) => new Date(t.updatedAt).getTime() || 0;
  if (sort === "edited") return out.sort((a, b) => time(b) - time(a));
  if (sort === "papers") return out.sort((a, b) => b.documentCount - a.documentCount || time(b) - time(a));
  // Untitled tables go last: a name sort is for finding a name.
  const name = (t: TableSummary) => t.caption.trim().toLocaleLowerCase();
  return out.sort((a, b) => {
    const x = name(a), y = name(b);
    if (!x !== !y) return x ? -1 : 1;
    return x.localeCompare(y) || time(b) - time(a);
  });
}

/** Fired when a table is deleted or restored, so every view of the list reloads. */
export const TABLES_CHANGED = "lilia:tables-changed";

export function useTables(fetchWithAuth: (u: string, i?: RequestInit) => Promise<Response>) {
  const [tables, setTables] = useState<TableSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/tables");
      if (!res.ok) throw new Error(`Could not load your tables (${res.status})`);
      setTables((await res.json()) as TableSummary[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your tables");
      // Null means "still unknown", which is not the same as "you have none" —
      // an empty list would show the start page and invite a second table.
      setTables(null);
    }
  }, [fetchWithAuth]);

  useEffect(() => { void load(); }, [load]);

  // Another view of the same tables changed them (the list deleted one, Trash
  // restored one): the Tables rail's counts follow.
  useEffect(() => {
    const onChange = () => { void load(); };
    window.addEventListener(TABLES_CHANGED, onChange);
    return () => window.removeEventListener(TABLES_CHANGED, onChange);
  }, [load]);

  const remove = useCallback(async (id: string) => {
    const res = await fetchWithAuth(`/api/tables/${id}`, { method: "DELETE" });
    if (res.ok) {
      setTables((ts) => (ts ?? []).filter((t) => t.id !== id));
      window.dispatchEvent(new CustomEvent(TABLES_CHANGED));
    }
    return res.ok;
  }, [fetchWithAuth]);

  /** Rename: the caption is the table's name. The whole table goes back, as
   *  the API's PUT takes it; linked papers take the new caption too. */
  const rename = useCallback(async (t: TableSummary, caption: string) => {
    const res = await fetchWithAuth(`/api/tables/${t.id}`, {
      method: "PUT",
      body: JSON.stringify({ caption, label: t.label, content: t.content }),
    });
    if (res.ok) {
      setTables((ts) => (ts ?? []).map((x) => (x.id === t.id ? { ...x, caption, updatedAt: new Date().toISOString() } : x)));
      window.dispatchEvent(new CustomEvent(TABLES_CHANGED));
    }
    return res.ok;
  }, [fetchWithAuth]);

  /** A table of its own with the same content, remembering where it came
   *  from (`copiedFrom`), as a copy made at Use in paper does. Its id, or null. */
  const duplicate = useCallback(async (t: TableSummary): Promise<string | null> => {
    const name = t.caption.trim();
    const res = await fetchWithAuth("/api/tables", {
      method: "POST",
      body: JSON.stringify({ caption: name ? `${name} (copy)` : "", label: "", content: t.content, copiedFrom: t.id }),
    });
    if (!res.ok) return null;
    const made = (await res.json()) as { id: string };
    window.dispatchEvent(new CustomEvent(TABLES_CHANGED));
    return made.id;
  }, [fetchWithAuth]);

  return { tables, error, reload: load, remove, rename, duplicate };
}
